import * as angular from 'angular';
import * as dayjs from 'dayjs';
import * as _ from 'lodash';

import * as utils from '../../utils';
import { PasteService } from '../services/paste-service';

// Declare a global extension to add the date picker
declare global {
    // tslint:disable-next-line: interface-name
    interface JQuery {
        fdatepicker(options?: any): JQuery;
    }

    interface IDatepickerOptions {
        startDate?: Date;
        endDate?: Date;
        minView?: number;
        maxView?: number;
        format?: string;
        startView?: number;
    }
}

type InputValue = number | string | Date;

interface IInputInvariant {
    type: string;
    prefix?: string;
    suffix?: string;
    maxLength?: number;
    init?: () => void;
    reload?: () => void;
    update?: (value: InputValue) => InputValue;
    validate?: (value: string) => boolean;
    format?: (value: InputValue) => string;
    parse?: (value: string) => InputValue;
}

interface IInputContext {
    scope: IInputScope;
    input: JQuery;
    controller: angular.INgModelController;
    initialized: boolean;
    editing: boolean;
}

interface IInputScope extends angular.IScope {
    invariant: string;
    type: string;
    prefix: string;
    suffix: string;
    placeholder: string;
    min: InputValue;
    max: InputValue;
    maxLength?: number;
    decimalPlaces: number;
    allowNegative: boolean;
    format: boolean;
    value: InputValue;
    model: InputValue;
    hasPrefix: () => boolean;
    hasSuffix: () => boolean;
    change: (value: any) => void;
    enterPressed: () => void;
}

class InputDirective implements angular.IDirective {
    readonly replace = true;
    readonly transclude = true;
    readonly restrict = 'E';
    readonly scope = {
        invariant: '@',
        placeholder: '@?',
        min: '=?',
        max: '=?',
        decimalPlaces: '=?',
        allowNegative: '=?',
        format: '=?',
        disabled: '=?ngDisabled',
        model: '=ngModel',
        change: '&?ngChange',
        enterPressed: '&?enterPressed'
    };

    readonly require = 'ngModel';
    readonly template = `
        <div class="fc-input" ng-class="{ 'fc-input-has-prefix': hasPrefix(), 'fc-input-has-suffix': hasSuffix() }">
            <div class="fc-input-group input-group">
                <span class="fc-input-prefix input-group-label">{{ prefix }}</span>
                <input type="{{ type }}" class="fc-input-{{ invariant }}" placeholder="{{ placeholder }}" maxLength="{{ maxLength }}" ng-disabled="disabled" ng-model="value" ng-change="change(value)" />
                <span class="fc-input-suffix input-group-label">{{ suffix }}</span>
            </div>
        </div>
    `;

    readonly link: angular.IDirectiveLinkFn;

    constructor(
        private readonly filter: angular.IFilterService,
        private readonly timeout: angular.ITimeoutService,
        private readonly pasteService: PasteService
    ) {
        this.link = this._link.bind(this);
    }

    private _link(
        scope: IInputScope,
        element: JQuery,
        _attributes: angular.IAttributes,
        controller: angular.INgModelController
    ): void {
        const input = element.find('input');
        const context = {
            scope,
            input,
            controller,
            initialized: false,
            editing: false
        };

        // Resolve whether a preconfiguration is in place for the invariant
        const invariant = this.mapInvariant(scope, context);

        if (!invariant) {
            scope.invariant = 'text';
        }

        invariant.type = invariant.type || 'text';
        invariant.init = invariant.init || angular.noop;
        invariant.reload = invariant.reload || angular.noop;
        invariant.update = invariant.update || (value => value);
        invariant.validate = invariant.validate || (() => true);
        invariant.format = invariant.format || (value => (value ? `${value}` : null));
        invariant.parse = invariant.parse || (value => value);

        // Configure the scope from the defined invariant
        scope.type = invariant.type;
        scope.prefix = invariant.prefix;
        scope.suffix = invariant.suffix;
        scope.maxLength = invariant.maxLength;

        scope.value = invariant.format(scope.model);

        scope.hasPrefix = () => !!scope.prefix;
        scope.hasSuffix = () => !!scope.suffix;

        // Run the init after the current scope digest so jquery plugins can be applied
        this.timeout(() => invariant.init());

        // Handle and parse when the value changes
        const change = scope.change || angular.noop;
        scope.change = value => {
            let model = null;
            let valid = true;

            // If value is empty continue with a null value
            if (_.trim(value) === '') {
                controller.$setViewValue(null);
            } else {
                // Validate the value entered
                valid = invariant.validate(value);

                if (valid) {
                    // Apply any updates if needed and parse the value
                    model = invariant.update(value);
                    model = invariant.parse(`${model}`);

                    // Reassign the value back to the model controller
                    controller.$setViewValue(model);
                }
            }

            // Invoke the external change handler
            change({ value: model, valid });
        };

        // Rebind the input model to an internal model for processing
        scope.$watch(
            () => scope.min,
            () => invariant.reload()
        );

        scope.$watch(
            () => scope.max,
            () => invariant.reload()
        );

        scope.$watch(
            () => controller.$modelValue,
            value => {
                scope.value = invariant.format(value);
                invariant.reload();
            }
        );

        // Bind any input event to process editing
        input.on('focus', () => this.setEditing(true, invariant, context));
        input.on('blur', () => this.setEditing(false, invariant, context));

        scope.$on('$destroy', () => input.off());
    }

    private mapInvariant(scope: IInputScope, context: IInputContext): IInputInvariant {
        switch (scope.invariant) {
            case 'number': {
                return {
                    type: 'text',
                    maxLength: 16,
                    init: () => this.initNumericInput(context, scope.allowNegative ?? false),
                    validate: value => this.validateNumber(value),
                    format: value => this.formatNumber(value, context),
                    parse: value => this.parseNumber(value)
                };
            }

            case 'currency': {
                return {
                    type: 'text',
                    prefix: '$',
                    maxLength: 16,
                    init: () => this.initNumericInput(context, scope.allowNegative ?? false),
                    validate: value => this.validateNumber(value),
                    format: value => this.formatNumber(value, context),
                    parse: value => this.parseNumber(value)
                };
            }

            case 'percent': {
                return {
                    type: 'text',
                    suffix: '%',
                    maxLength: 6,
                    init: () => this.initNumericInput(context, scope.allowNegative ?? true),
                    validate: value => this.validateNumber(value),
                    format: value => this.formatNumber(value, context),
                    parse: value => this.parseNumber(value)
                };
            }

            case 'date': {
                return {
                    type: 'text',
                    maxLength: 10,
                    init: () => this.initDateInput(context),
                    reload: () => this.initDateInput(context),
                    update: value => this.updateDate(value, context),
                    validate: value => this.validateDate(value),
                    format: value => (value ? utils.asDateString(`${value}`) : null),
                    parse: value => utils.asDate(value)
                };
            }

            case 'month': {
                return {
                    type: 'text',
                    maxLength: 10,
                    init: () => this.initDateInput(context, true),
                    reload: () => this.initDateInput(context, true),
                    update: value => this.updateDate(value, context),
                    validate: value => utils.isDate(value),
                    format: value => (value ? utils.asDateString(`${value}`, 'MMM YYYY') : null),
                    parse: value => utils.asDate(value)
                };
            }

            default: {
                return null;
            }
        }
    }

    private initNumericInput(context: IInputContext, allowNegative: boolean): void {
        if (!context.initialized) {
            context.initialized = true;

            // Event handlers
            context.input.on('keypress', (event: JQuery.KeyPressEvent) => {
                // If the enter key is pressed broadcast an event
                const char = utils.getCharCode(event);
                if (char === 13) {
                    const enterPressed = context.scope.enterPressed || angular.noop;
                    enterPressed();
                } else {
                    // Otherwise validate the char
                    this.validateNumericChar(event, allowNegative);
                }
            });

            context.input.on('paste', (event: any) => {
                // Get the pasted value, format it and apply to the input
                const value = this.pasteService.getData(event);
                const formatted = this.formatNumber(value, context);

                context.scope.value = formatted;

                // Trigger the change event
                context.scope.change(formatted);
            });
        }
    }

    private initDateInput(context: IInputContext, onlyMonths?: boolean): void {
        if (context.initialized) {
            context.input.fdatepicker('remove');
        }

        // Configure the date picker
        const options: IDatepickerOptions = {};

        if (onlyMonths) {
            options.minView = 3;
            options.format = "M yyyy"
            options.startView = 3;
        }

        if (utils.isDate(context.scope.min)) {
            options.startDate = utils.asDate(context.scope.min);
        }

        if (utils.isDate(context.scope.max)) {
            options.endDate = utils.asDate(context.scope.max);
        }

        context.initialized = true;
        context.input.fdatepicker(options);
    }

    private updateDate(value: InputValue, context: IInputContext): InputValue {
        // Check the value is within the min/max range
        if (utils.isDate(value)) {            
            const date = dayjs(value);

            if (utils.isDate(context.scope.min)) {
                const min = dayjs(context.scope.min);

                if (date.isBefore(min)) {
                    value = context.scope.min;
                } else if (utils.isDate(context.scope.max)) {
                    const max = dayjs(context.scope.max);

                    if (date.isAfter(max)) {
                        value = context.scope.max;
                    }
                }
            } else if (utils.isDate(context.scope.max)) {
                const max = dayjs(context.scope.max);

                if (date.isAfter(max)) {
                    value = context.scope.max;
                }
            }
        }

        return value;
    }

    private setEditing(editing: boolean, invariant: IInputInvariant, context: IInputContext): void {
        context.editing = editing;

        // Check the value validity
        let valid = invariant.validate(`${context.scope.value}`);
        if (editing) {
            const value = invariant.format(context.scope.value);
            valid = invariant.validate(`${value}`);
        } else {
            // If unediting, check if any adjustments are needed
            if (valid) {
                context.scope.value = invariant.update(context.scope.value);
            }
        }

        // If valid format the value for the editing state
        if (valid) {
            context.scope.value = invariant.format(context.scope.value);
        }

        // Tell angular to apply the scope changes
        context.scope.$apply();
    }

    private validateNumber(value: string): boolean {
        return utils.isNumber(value);
    }

    private validateDate(value: string): boolean {
        // Use a regex to check the format of the entered date
        const regex = /^(((0)?[0-9])|((1)[0-2]))(\/)([0-2]?[0-9]|(3)[0-1])(\/)\d{4}$/;
        return regex.test(value) && utils.isDate(value);
    }

    private formatNumber(value: any, context: IInputContext): string {
        // Remove any commas that might have been included in a previous format
        value = `${value}`;
        value = value.replace(/\,/g, '');

        // Get the number value
        if (utils.isNumber(value)) {
            value = utils.asNumber(value);

            if (!context.editing) {
                // If not editing check the value is within the min/max ranges
                const min = utils.asNumber(context.scope.min);
                const max = utils.asNumber(context.scope.max);

                if (utils.isNumber(min) && value < min) {
                    value = min;
                } else if (utils.isNumber(max) && value > max) {
                    value = max;
                }

                context.scope.value = `${value}`;

                // Then format the value as needed
                if (context.scope.format !== false) {
                    const filter = this.filter('number');
                    return filter(value, context.scope.decimalPlaces || 0);
                }
            }

            return `${value}`;
        }

        return null;
    }

    private parseNumber(value: any): number {
        return utils.isNumber(value) ? utils.asNumber(value) : null;
    }

    private validateNumericChar(event: JQuery.KeyPressEvent, allowNegative: boolean): void {
        const numeric = utils.isCharNumeric(event, allowNegative);
        if (!numeric) {
            event.preventDefault();
            event.stopPropagation();
        }
    }
}

const InputDirectiveFactory: angular.IDirectiveFactory = (
    filter: angular.IFilterService,
    timeout: angular.ITimeoutService,
    pasteService: PasteService
) => new InputDirective(filter, timeout, pasteService);

InputDirectiveFactory.$inject = ['$filter', '$timeout', 'fcPasteService'];

export { InputDirectiveFactory };
